import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { ExternalLink, Pencil } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Header from "../components/dashboard/Header";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { fmtCurrency, fmtPeriod } from "../lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const REPORT_ID = "f4166715-4848-4b5f-ad80-5af0f23d8055";
const EMBED_URL = `https://lookerstudio.google.com/embed/reporting/${REPORT_ID}/page/p_0`;
const REPORT_URL = `https://lookerstudio.google.com/reporting/${REPORT_ID}`;

const num = (n) => (n == null ? "—" : Number(n).toLocaleString());
const money2 = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (n) => (n == null ? "—" : `${Number(n).toFixed(2)}%`);

function Kpi({ index, label, value, sub }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.4 }}
      className="bg-brand-surface border border-brand-border px-6 py-5" data-testid={`mkt-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <p className="text-[11px] uppercase tracking-[0.15em] text-brand-tertiary">{label}</p>
      <p className="font-mono text-2xl text-brand-ink mt-2">{value}</p>
      {sub && <p className="text-[11px] text-brand-tertiary mt-1">{sub}</p>}
    </motion.div>
  );
}

export default function Marketing() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const isAdmin = user?.role === "admin";

  const load = useCallback(() => api.get("/marketing").then(({ data }) => {
    setData(data);
    setPeriod((p) => p || data.latest_period || "");
  }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  if (user && !["admin", "regional"].includes(user.role)) return <Navigate to="/sales" replace />;

  const current = data?.months.find((m) => m.period === period);

  const openEdit = () => { setForm(current || { period }); setEditOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, period };
      ["spend", "conversions", "cpa", "clicks", "impressions", "ctr", "conv_rate", "avg_cpc"].forEach((k) => {
        body[k] = body[k] === "" || body[k] == null ? null : Number(body[k]);
      });
      await api.put(`/marketing/${period}`, body);
      toast.success("Marketing figures updated");
      setEditOpen(false);
      await load();
    } catch (err) {
      toast.error("Couldn't save", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const efield = "w-full bg-transparent border-0 border-b border-brand-borderStrong py-2 text-sm font-mono text-brand-ink focus:outline-none focus:border-brand-primary";

  return (
    <div className="min-h-screen bg-brand-page relative z-10">
      <Header />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary">Paid Media · PPC {period ? `· ${fmtPeriod(period)}` : ""}</p>
            <h1 className="font-serif text-4xl lg:text-5xl font-light tracking-tight text-brand-ink mt-2">Marketing</h1>
            <p className="text-sm text-brand-muted mt-1">Google Ads performance — clicks, conversions, cost and CPA.</p>
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            {(data?.selectable_periods?.length > 0 || data?.periods?.length > 0) && (
              <div>
                <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1">Month</label>
                <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="mkt-period"
                  className="bg-brand-surface border border-brand-border rounded-md px-3 py-2 text-sm text-brand-ink focus:outline-none focus:border-brand-primary">
                  {[...(data?.selectable_periods?.length ? data.selectable_periods : data.periods)].reverse().map((p) => (
                    <option key={p} value={p}>{fmtPeriod(p)}{!data.periods.includes(p) ? " — no figures" : ""}</option>
                  ))}
                </select>
              </div>
            )}
            {isAdmin && (
              <div>
                <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1">Add / jump to month</label>
                <input type="month" value={period} onChange={(e) => e.target.value && setPeriod(e.target.value)} data-testid="mkt-add-month"
                  className="bg-brand-surface border border-brand-border rounded-md px-3 py-2 text-sm text-brand-ink focus:outline-none focus:border-brand-primary" />
              </div>
            )}
            {isAdmin && period && (
              <button onClick={openEdit} data-testid="mkt-edit"
                className="flex items-center gap-2 text-xs text-brand-muted hover:text-brand-ink border border-brand-border hover:border-brand-borderStrong rounded-full px-4 py-2 transition-colors duration-200">
                <Pencil className="h-3.5 w-3.5" /> {current ? "Edit figures" : "Add figures"}
              </button>
            )}
            <a href={REPORT_URL} target="_blank" rel="noopener noreferrer" data-testid="marketing-open-report"
              className="flex items-center gap-2 text-xs text-brand-muted hover:text-brand-ink border border-brand-border hover:border-brand-borderStrong rounded-full px-4 py-2 transition-colors duration-200">
              <ExternalLink className="h-3.5 w-3.5" /> Open full report
            </a>
          </div>
        </div>

        {current ? (
          <div className="space-y-2.5" data-testid="mkt-kpis-wrap">
            <div className="flex items-center gap-2" data-testid="mkt-freshness">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-gold" />
              <p className="text-[11px] uppercase tracking-[0.14em] text-brand-tertiary">
                As of {fmtPeriod(period)} · manually entered figures
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-brand-border border border-brand-border" data-testid="mkt-kpis">
              <Kpi index={0} label="Ad Spend" value={fmtCurrency(current.spend)} sub="for the month" />
              <Kpi index={1} label="Conversions" value={num(current.conversions)} sub={`${pct(current.conv_rate)} conv. rate`} />
              <Kpi index={2} label="CPA" value={money2(current.cpa)} sub="cost per acquisition" />
              <Kpi index={3} label="Clicks" value={num(current.clicks)} sub={`${money2(current.avg_cpc)} avg CPC`} />
              <Kpi index={4} label="Impressions" value={num(current.impressions)} sub={`${pct(current.ctr)} CTR`} />
            </div>
          </div>
        ) : period ? (
          <div className="bg-brand-surface border border-dashed border-brand-border px-6 py-12 text-center" data-testid="mkt-empty">
            <p className="font-serif text-2xl text-brand-ink">No figures entered for {fmtPeriod(period)}</p>
            <p className="text-sm text-brand-muted mt-1.5">
              {isAdmin ? "Click \u201cAdd figures\u201d above to enter this month\u2019s PPC numbers." : "These numbers haven\u2019t been entered yet."}
            </p>
          </div>
        ) : null}

        <div className="bg-brand-surface border border-brand-border overflow-hidden" data-testid="marketing-embed">
          <iframe
            title="Bond Collective PPC Reporting"
            src={EMBED_URL}
            className="w-full"
            style={{ height: "1400px", border: 0 }}
            allowFullScreen
            sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          />
        </div>
        <p className="text-xs text-brand-tertiary">
          The cards above are editable monthly figures; the report below is live from Looker Studio. If it doesn't load, enable embedding in Looker Studio (File → Embed report), or use "Open full report".
        </p>
      </main>

      {isAdmin && (
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="bg-brand-page border-brand-border" data-testid="mkt-edit-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl font-light text-brand-ink">Marketing — {fmtPeriod(period)}</DialogTitle>
              <DialogDescription className="text-sm text-brand-muted">Update the monthly PPC figures shown on the cards.</DialogDescription>
            </DialogHeader>
            <form onSubmit={save} className="grid grid-cols-2 gap-4 pt-2">
              {[["spend", "Ad Spend ($)"], ["conversions", "Conversions"], ["cpa", "CPA ($)"], ["clicks", "Clicks"],
                ["impressions", "Impressions"], ["avg_cpc", "Avg CPC ($)"], ["ctr", "CTR (%)"], ["conv_rate", "Conv. Rate (%)"]].map(([k, lbl]) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">{lbl}</label>
                  <input type="number" step="any" value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`mkt-field-${k}`} className={efield} />
                </div>
              ))}
              <DialogFooter className="col-span-2">
                <button type="submit" data-testid="mkt-save"
                  className="w-full bg-brand-primary text-brand-page py-3 text-sm tracking-wide hover:bg-brand-primaryHover active:scale-[0.98] transition-all">
                  Save Figures
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
